import React, { useEffect, useRef, useState } from "react";
import "../../styles/Support.scss";

interface SupportItem {
  id: number;
  name: string;
  logo: string;
}

interface SponsorItem extends SupportItem {
  level: string;
}

interface SupportJson {
  support: SupportItem[];
  sponsor: SponsorItem[];
}

const Support: React.FC = () => {
  const [data, setData] = useState<SupportJson>({
    support: [],
    sponsor: [],
  });

  useEffect(() => {
    fetch("/resources/support.json")
      .then((res) => res.json())
      .then(setData)
      .catch(console.error);
  }, []);

  const platinumSponsors = data.sponsor.filter((s) => s.level === "platinum");
  const silverSponsors = data.sponsor.filter((s) => s.level === "silver");
  const bronzeSponsors = data.sponsor.filter((s) => s.level === "bronze");

  const useAutoScroll = (ref: React.RefObject<HTMLDivElement | null>) => {
    const direction = useRef<"left" | "right">("right");

    useEffect(() => {
      const slider = ref.current;
      if (!slider) return;

      let frame: number;
      const speed = 1.5;

      const scroll = () => {
        if (direction.current === "right") {
          slider.scrollLeft += speed;
          if (slider.scrollLeft + slider.clientWidth >= slider.scrollWidth) {
            direction.current = "left";
          }
        } else {
          slider.scrollLeft -= speed;
          if (slider.scrollLeft <= 0) {
            direction.current = "right";
          }
        }
        frame = requestAnimationFrame(scroll);
      };

      frame = requestAnimationFrame(scroll);
      return () => cancelAnimationFrame(frame);
    }, [ref]);
  };

  const Slider: React.FC<{ items: SupportItem[] }> = ({ items }) => {
    const sliderRef = useRef<HTMLDivElement>(null);
    useAutoScroll(sliderRef);

    return (
      <div className="support-slider" ref={sliderRef}>
        {items.map((item) => (
          <div key={item.id} className="support-logo">
            <img src={item.logo} alt={item.name} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <section className="support-section">
      <h2>Supported By</h2>
      <Slider items={data.support} />

      {platinumSponsors.length > 0 && (
        <>
          <h2>Platinum Sponsor</h2>
          <Slider items={platinumSponsors} />
        </>
      )}

      {silverSponsors.length > 0 && (
        <>
          <h2>Silver Sponsor</h2>
          <Slider items={silverSponsors} />
        </>
      )}

      {bronzeSponsors.length > 0 && (
        <>
          <h2>Bronze Sponsor</h2>
          <Slider items={bronzeSponsors} />
        </>
      )}

      <div className="support-divider" />
    </section>
  );
};

export default Support;
